import { Inject, Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Config, MW_APP_CONFIG } from '../../config';
import { MwHttpService } from '../../http/services';
import {
  CompanySettingsApiModel,
  FeatureFlagViewModel,
  MwCorePreferenceModel,
  UserPreferenceViewModel,
} from '../models';
import { MwCorePreferenceService } from './preference.service';

@Injectable()
export class MwCompanyApiService {
  constructor(
    private http: MwHttpService,
    private preferenceService: MwCorePreferenceService,
    @Inject(MW_APP_CONFIG) private config: Config
  ) {}
  hasLogo(companyId: number): Observable<boolean> {
    return this.http.authGet<boolean>(
      `/integration/v1/companies/${companyId}/hasLogo`
    );
  }

  getSettings(companyId: number): Observable<CompanySettingsApiModel> {
    return this.http.authGet<CompanySettingsApiModel>(
      `/integration/v1/companies/${companyId}/settings`
    );
  }

  hasFeatureFlag(key: string, companyId: number): Observable<boolean> {
    return this.http
      .authGet<boolean>(
        `/integration/v1/companies/${companyId}/featureFlags/has/${key}`,
        {
          ignoreNotifyStatusCodes: [400, 404, 401, 403],
        }
      )
      .pipe(
        catchError(() =>
          this.getFeatureFlag(key, companyId).pipe(
            map((t) => t.featureFlagId > 0)
          )
        )
      );
  }

  getFeatureFlag(
    key: string,
    companyId: number
  ): Observable<FeatureFlagViewModel> {
    const endpoint =
      companyId > 0
        ? `/integration/v1/companies/${companyId}/featureFlags`
        : '/integration/v1/featureFlags';
    const query = `?$select=FeatureFlagId&$filter=Key eq '${key}'`;

    return this.http
      .authGet<FeatureFlagViewModel[]>(endpoint + query, {
        ignoreNotifyStatusCodes: [404, 401, 403, 400],
      })
      .pipe(
        map((featureFlags) => featureFlags[0]),
        catchError(() => of({ featureFlagId: 0 }))
      );
  }

  savePreference(data: any): Observable<number> {
    const key = this.getPreferenceKey();
    if (typeof data !== 'string') {
      data = JSON.stringify(data);
    }

    const preference: UserPreferenceViewModel = {
      key,
      data,
    };
    return this.http.authPost<number>(
      `/integration/v1/Companies/{companyId}/Users/preference`,
      { body: preference }
    );
  }

  getPreferences(companyId: number): Observable<MwCorePreferenceModel> {
    const filter = `$filter=` + `(Key eq '${this.getPreferenceKey()}')`;
    return this.loadFilteredPreference(companyId, filter).pipe(
      map((t) => t[0] && this.parseJson(t[0].data)),
      map((t) => this.preferenceService.map(t))
    );
  }

  clearAllPreference(): Observable<any> {
    return this.http.authDelete<any>(
      `/integration/v1/Companies/{companyId}/Users/preference`
    );
  }

  private loadFilteredPreference(
    companyId: number,
    oDataFilter: string
  ): Observable<UserPreferenceViewModel[]> {
    return this.http.authGet<UserPreferenceViewModel[]>(
      `/integration/v1/Companies/${companyId}/Users/preference?${oDataFilter}`
    );
  }

  private parseJson(value: string): any {
    if (value) {
      try {
        return JSON.parse(value);
      } catch {}
    }

    return {};
  }

  private getPreferenceKey(): string {
    const keys = this.config.environment.preferenceKey || {};

    return keys['general'] || 'default';
  }
}
